import React from "react";
import {
  Button,
  ResponsiveSanityImage,
  ResponsiveSanityImageProps
} from "@silversea-ssc/ui-components-react";
import { Trans } from "@lingui/macro";
import CruiseRecap from "components/CruiseInfoSummary/CruiseRecap";
import PriceFromBox from "components/PriceFromBox/PriceFromBox";
import { SectionContainer } from "@silversea-ssc/ui-components-react";
import { CruisePriceFrom } from "types/api/suite_price";
import * as styles from "./SpecialVoyageInfoSummary.module.scss";

import PricePromos from "components/PriceFromBox/PricePromos";
import { LocationSpecificOffer } from "components/useLocationSpecificOffers/types";
import { useGeolocation } from "src/utils/localization/geolocation/GeolocationContext";
import showExchangeDisclaimer from "src/utils/showExchangeDisclaimer";
import ExchangeRateDisclaimer from "components/PriceFromBox/ExchangeRateDisclaimer";

export interface SpecialVoyageInfoSummaryProps {
  map?: ResponsiveSanityImageProps["image"];
  heroImage?: ResponsiveSanityImageProps["image"];
  cruiseCode?: string;
  cruiseAbstract?: string;
  cruiseName?: string | JSX.Element;
  cruiseTitle?: string;
  price?: CruisePriceFrom;
  // destinationName?: string;
  // destinationFullPath?: string;
  shipName?: string;
  departureDate?: string | Date;
  arrivalDate?: string | Date;
  duration?: number;
  onClickSuitesAndFaresButton?: () => void;
  onClickOpenItineraryMap?: () => void;
  raqUrl?: string;
  indexPage?: {
    url?: string;
    title?: JSX.Element;
  };
  offers?: LocationSpecificOffer[];
}

const SpecialVoyageInfoSummary: React.FC<SpecialVoyageInfoSummaryProps> = ({
  heroImage,
  cruiseName,
  cruiseAbstract,
  cruiseTitle,
  shipName,
  departureDate,
  arrivalDate,
  duration,
  price,
  map,
  onClickSuitesAndFaresButton,
  raqUrl,
  indexPage,
  onClickOpenItineraryMap,
  offers
}) => {
  const geoLocation = useGeolocation();
  const exchangeDisclaimer = showExchangeDisclaimer(geoLocation);

  return (
    <React.Fragment>
      {heroImage && (
        <ResponsiveSanityImage
          className={styles.backgroundImage}
          image={heroImage}
          defaultWidth={1024}
          defaultHeight={600}
        />
      )}
      <SectionContainer
        backgroundColor="destination"
        spacingBottom="regular"
        dataContentName="special_voyage_intro"
      >
        <div className={styles.box}>
          <div className={styles.header}>
            <Button
              variant="link"
              renderAs="a"
              href={indexPage?.url}
              dataContentPiece="view_all_cruises_topleft"
            >
              {indexPage?.title}
            </Button>
          </div>
          <div className={styles.infoContainer}>
            <h3 className={styles.destinationName}>
              {cruiseAbstract} - {cruiseName}
            </h3>
            <h1 className={styles.cruiseName}>{cruiseTitle}</h1>

            <CruiseRecap
              shipName={shipName}
              departureDate={departureDate}
              arrivalDate={arrivalDate}
              duration={duration}
            />
            <PriceFromBox price={price} className={styles.price}>
              {exchangeDisclaimer && <ExchangeRateDisclaimer />}
              {offers && <PricePromos offers={offers} variant="list" />}
            </PriceFromBox>

            <div className={styles.disclaimer}>
              <Trans>Fares shown are per Guest</Trans>
            </div>
            <div className={styles.ctaButtons}>
              <Button
                className={styles.cta}
                variant="primary"
                centerText
                href={raqUrl}
                dataContentPiece="request_a_quote"
              >
                <Trans>Request a Quote</Trans>
              </Button>
              <Button
                className={styles.cta}
                variant="link"
                centerText
                renderAs="button"
                onClick={
                  onClickSuitesAndFaresButton
                    ? () => onClickSuitesAndFaresButton()
                    : undefined
                }
                dataContentPiece="suites_and_fares"
              >
                <Trans>Suites & Fares</Trans>
              </Button>
            </div>
          </div>
          <div className={styles.footer}>
            {map && (
              <Button
                variant="secondary"
                renderAs="button"
                onClick={onClickOpenItineraryMap}
                dataContentPiece="view_itinery_map"
              >
                <Trans>VIEW ITINERARY MAP</Trans>
              </Button>
            )}
          </div>
        </div>
      </SectionContainer>
    </React.Fragment>
  );
};

export default SpecialVoyageInfoSummary;
